import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForgotPasswordMutation } from '@/redux/internal-services/onboardingApi';
import LeftSidePanel from '@/components/UI/LeftSidePanel';
import { Button } from '@/components/UI/Button';
import { fieldClass } from '@/components/UI/fieldStyles';

type PageState = 'form' | 'sent' | 'google_account';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

  const [email, setEmail] = useState('');
  const [pageState, setPageState] = useState<PageState>('form');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await forgotPassword({ email }).unwrap();
      setPageState('sent');
    } catch (err: unknown) {
      const apiErr = err as { data?: { message_code?: number } };
      if (apiErr?.data?.message_code === 1118) {
        setPageState('google_account');
        return;
      }
      setError(t('common.error', 'Something went wrong. Please try again.'));
    }
  };

  return (
    <div className="auth-bg flex min-h-dvh relative overflow-hidden">
      <LeftSidePanel />
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">

          {pageState === 'sent' && (
            <div className="card-surface px-10 py-10 text-center">
              <div className="w-16 h-16 bg-accent-soft rounded-full flex items-center justify-center mx-auto mb-6">
                <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-accent-text" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-h2 text-ui-text mb-2">
                {t('auth.checkYourEmail', 'Check Your Email')}
              </h2>
              <p className="text-ui-text2 text-sm mb-1">
                {t('auth.resetEmailSentTo', 'We sent a password reset link to:')}
              </p>
              <p className="font-semibold text-ui-text text-sm mb-6">{email}</p>
              <p className="text-ui-text3 text-xs mb-8">
                {t('auth.resetEmailNote', "Didn't receive it? Check your spam folder or try again.")}
              </p>
              <Button size="lg" className="w-full mb-4"
                type="button"
                onClick={() => { setPageState('form'); setEmail(''); }}>
                {t('auth.tryAgain', 'Try Again')}
              </Button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-accent-text hover:underline font-medium"
              >
                {t('auth.backToLogin', 'Back to login')}
              </button>
            </div>
          )}

          {pageState === 'google_account' && (
            <div className="card-surface px-10 py-10 text-center">
              {/* Social login icon */}
              <div className="w-16 h-16 bg-ui-subtle rounded-full flex items-center justify-center mx-auto mb-6">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-8 h-8 text-ui-text2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h2 className="text-h2 text-ui-text mb-2">
                {t('auth.socialAccountTitle', 'Social Login Detected')}
              </h2>
              <p className="text-ui-text2 text-sm mb-2">
                {t('auth.socialAccountDesc', 'This email is linked to a social login account.')}
              </p>
              <p className="font-semibold text-ui-text text-sm mb-6">{email}</p>
              <p className="text-ui-text3 text-xs mb-8">
                {t('auth.socialAccountNote', 'Password reset is not available for social login accounts. Please use your social provider (Google, etc.) to sign in.')}
              </p>
              <Button size="lg" className="w-full mb-4"
                type="button"
                onClick={() => navigate('/login')}>
                {t('auth.goToLogin', 'Go to Login')}
              </Button>
              <button
                type="button"
                onClick={() => { setPageState('form'); setEmail(''); }}
                className="text-sm text-ui-text3 hover:text-ui-text2 hover:underline"
              >
                {t('auth.tryDifferentEmail', 'Try a different email')}
              </button>
            </div>
          )}

          {pageState === 'form' && (
            <>
              {/* Back link */}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="flex items-center gap-1.5 text-sm text-ui-text2 hover:text-accent-text mb-8 transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                {t('auth.backToLogin', 'Back to login')}
              </button>

              <h1 className="text-h2 text-ui-text mb-2">
                {t('auth.forgotPassword', 'Forgot Password?')}
              </h1>
              <p className="text-ui-text2 text-sm mb-8">
                {t('auth.forgotPasswordDesc', "No worries — enter your email and we'll send you a reset link.")}
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-ui-text mb-1.5">
                    {t('auth.email', 'Email address')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    className={fieldClass()}
                  />
                </div>

                {error && (
                  <p className="text-danger-text text-sm">{error}</p>
                )}

                <Button size="lg" className="w-full"
                  type="submit"
                  disabled={isLoading}>
                  {isLoading ? t('common.loading', 'Sending…') : t('auth.sendResetLink', 'Send Reset Link')}
                </Button>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
